import { DecimalPipe } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment-mini';
import {
  backOrderStyle, BACKORDER_STATUS, comingSoonStyle, inStockStyle,
  onShopStyle, outOfStockStyle, thaiCalendarYrs
} from '../../shared/constants/constants';
import { dateRegex } from '../../shared/constants/regex-constants';
import { JwtToken } from '../../shared/models/jwt-token.model';
import { sharedConfig } from '../../shared/shared.config';
import { SharedConfig } from '../../shared/models/shared-config.model';

const defaultDateFormat = 'MMM DD, YYYY HH:mm:ss';
const defaultLocale = 'en-US';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  private static translateService: TranslateService;

  constructor(
    @Inject(sharedConfig) public config: SharedConfig,
    translate: TranslateService
  ) {
    UtilityService.translateService = translate;
  }

  /**
   *
   *
   * Static function to get the stock style class for product/entry
   *
   *
   *
   * @static
   * @param stockStatus Stock status of the product
   * @returns
   * @memberof UtilityService
   */
  static getStockLevelStyle(stockStatus: string) {
    let stockStyling = '';
    switch (stockStatus) {
      case 'outOfStock':
      case 'temporaryNotAvailable':
      case 'noLongerAvailable':
        stockStyling = outOfStockStyle;
        break;
      case 'backOrder':
        stockStyling = backOrderStyle;
        break;
      case 'inStock':
        stockStyling = inStockStyle;
        break;
      case 'notAvailableAtWarehouse':
        stockStyling = onShopStyle;
        break;
      case 'notYetAvailable':
        stockStyling = comingSoonStyle;
        break;
      default:
        stockStyling = '';
    }
    return stockStyling;
  }

  static getStockLevelStatus(stockLevelStatusName: string) {
    let stockLevelStatus = '';
    switch (stockLevelStatusName) {
      case 'noLongerAvailable':
        stockLevelStatus = 'NLA';
        break;

      case 'temporaryNotAvailable':
        stockLevelStatus = 'TNA';
        break;

      case 'notYetAvailable':
        stockLevelStatus = 'NYA';
        break;

      case 'backOrder':
        stockLevelStatus = BACKORDER_STATUS;
        break;

      case 'notAvailableAtWarehouse':
        UtilityService.translateService.get('SALE.NAW')
          .subscribe(translation => {
            stockLevelStatus = translation;
          });
        break;

      case 'outOfStock':
        stockLevelStatus = 'Out of Stock';
        break;

      default:
        stockLevelStatus = '';
    }

    return stockLevelStatus;
  }

  static getFormattedDateWithTZ(date: string, format?: string, presentDate?: boolean): string | null {
    if (date) {
      return moment.parseZone(date, 'YYYY-MM-DDTHH:mm:ssZ').format((format) ? (format) : (defaultDateFormat));
    } else if (presentDate) {
      return moment().format((format) ? (format) : (defaultDateFormat));
    } else {
      return '';
    }
  }

  static getFormattedNumber(inputNumber: number, format = '.2-2'): string | null {
    if (inputNumber >= 0 || !isNaN(inputNumber)) {
      const formattedNumber = new DecimalPipe(defaultLocale).transform(inputNumber, format).split(',').join('');
      return (formattedNumber);
    } else {
      return '';
    }
  }
  /**
   *
   *
   *
   *
   *
   *
   * @static
   * @param [length=5]
   * @returns
   * @memberof UtilityService
   */
  static getRandomAlphaNumStr(length = 5): string {
    let randomString = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      randomString += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return randomString;
  }

  /**
   *
   *
   * use this to convert Base64 encoded string into URL friendly,
   *
   * i.e. '+' and '/' are replaced with '-' and '_' also any trailing '='
   *
   * characters are removed
   *
   *
   *
   * @param str the encoded string
   * @returns the URL friendly encoded String
   */
  static base64EncodeUrlSafe(str: string): string {
    return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/\=+$/, '');
  }

  /**
   *
   *
   * use this to make a Base64 encoded string URL friendly,
   *
   * i.e. '+' and '/' are replaced with '-' and '_' also any trailing '='
   *
   * characters are removed
   *
   *
   *
   * @param str the encoded string
   * @returns the URL friendly encoded String
   */
  static getRandomURLSafeBase64Str(str: string): string {
    const base64Str = btoa(JSON.stringify({ nonce: str }));
    return this.base64EncodeUrlSafe(base64Str);
  }

  /**
   * @name parseJwt
   * @description this method is used to decode jwt token information
   * @returns parsed token object
   * @memberof AuthService
   */
  static parseJwt(token: string): JwtToken {
    let parsedToken: JwtToken = null;
    if (token) {
      const base64Url = token.split('.');
      if (base64Url.length >= 2) {
        const base64 = base64Url[1].replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) =>
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        parsedToken = JSON.parse(jsonPayload);
      }
    }
    return parsedToken;
  }

  /**
   *
   *
   * @name allowNumberOnly
   * @description this method allows to enter number only in input field
   * @static
   * @param event
   * @returns
   * @memberof UtilityService
   */
  static allowNumberOnly(event: any): boolean {
    return event.charCode >= 48 && event.charCode <= 57;
  }

  /**
   *
   *
   * convert dd/mm/yyyy date format to YYYY-MM-DD
   *
   * Also subtract the thai calender years to get the present year
   *
   *
   *
   * @param date
   * @param isThaiDate depicts if the Date is thai
   * @memberof UtilityService
   */
  static parseDate(date: string, isThaiDate: boolean = true) {
    const dateStr = date ? date.replace(/\s/g, '') : '';
    if (dateStr && dateRegex.test(dateStr)) {
      const dates = dateStr.split('/');
      let year = Number(dates[2]);
      if (isThaiDate) {
        year = year - thaiCalendarYrs;
      }
      return `${year}-${dates[1]}-${dates[0]}`;
    }
  }

  /**
   *
   *
   * @name validateUploadedFile
   * @description the method is used to validate upload file size and extension
   * @static
   * @param file file to be uploaded
   * @param maxFileSizeAllowed allowed file size in bytes
   * @param allowedFileTypes allowed file extensions
   * @returns errors
   * @memberof UtilityService
   */
  static validateUploadedFile(file: File, maxFileSizeAllowed: number, allowedFileTypes: string[]): any[] {
    const errors: any[] = [];
    if (file) {
      // validate file size
      if (file.size > maxFileSizeAllowed) {
        errors.push({ invalidFileSize: true });
      }

      // validate file ext
      if (!this.hasAllowedExtension(file.name, allowedFileTypes)) {
        errors.push({ invalidFileExt: true });
      }
      return errors;
    }
  }

  static hasAllowedExtension(filename: string, allowedFileTypes: string[]): boolean {
    return (new RegExp('(' + allowedFileTypes.join('|').replace(/\./g, '\\.') + ')$', 'i')).test(filename);
  }

  /**
   *
   *
   * Function to format string with hyphen at given hyphen index/positons
   *
   *
   *
   * @static
   * @param inputStr String to be formatted
   * @param hyphenIndexList List of hyphen index
   * @returns
   * @memberof UtilityService
   */
  static formatStringWithHyphen(inputStr: string, hyphenIndexList: number[]): string {
    inputStr = inputStr.replace(/-/g, '');
    const inputArr = Array.from(inputStr);
    if (inputArr && inputArr.length && hyphenIndexList && hyphenIndexList.length) {
      hyphenIndexList.forEach((index) => {
        if (index < inputArr.length) {
          inputArr.splice(index, 0, '-');
        }
      });
    }
    return inputArr.join().replace(/,/g, '');
  }

  /**
   *
   *
   * Function is used to replace all HTML tags with single space
   *
   *
   *
   * @static
   * @param inputStr
   * @returns
   * @memberof UtilityService
   */
  static replaceAllHtmlTagsWithSpace(inputStr: string) {
    return inputStr.replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' ').replace(/\u21b5/g, '').trim();
  }

  /**
   *
   *
   * @description to get the thai datef
   * @static
   * @param date
   * @memberof UtilityService
   */
  static getThaiDate(date: string, format?: string) {
    let thaiDate = date;
    if (date) {
      const splittedDate = date.split('-');
      if (splittedDate && splittedDate.length > 0) {
        const year = splittedDate[0];
        const thaiYear = parseInt(year, 10) + thaiCalendarYrs;
        splittedDate[0] = thaiYear.toString();
        thaiDate = splittedDate.join('-');
      }
    }
    if (format) {
      thaiDate = moment(thaiDate).format((format));
    }
    return thaiDate;
  }

  /**
   * @name appendZeroAtStart
   * @description function used to append zero ate start of string
   * @static
   * @param inputStr
   * @memberof UtilityService
   */
  static appendZeroAtStart(inputStr: string) {
    if (inputStr && inputStr.length > 0 && !inputStr.startsWith('0')) {
      inputStr = `0${inputStr}`;
    }
    return inputStr;
  }

  /**
   * @name getSessionId
   * @description generate unique session id.
   * @static
   */
  static getSessionId() {
    const str = '0123456789ZXCVBNMASDFGHJKLQWERTYUIOPmnbvcxzlkjhgfdsapoiuytrewq';
    let id = '';
    while (id.length < 40) {
      const position = Math.round(Math.random() * (str.length - 1));
      id += str[position];
    }
    return id;
  }

  /**
   *
   *
   * Function to sort an array by date
   *
   *
   *
   * @param dataToSort
   * @param sortBy
   * @memberof UtilityService
   */
  sortByDate(dataToSort: Array<any>, sortBy: string) {
    dataToSort.sort((obj1, obj2) => {
      const obj1Date = moment(obj1[sortBy]);
      const obj2Date = moment(obj2[sortBy]);

      if (obj1Date.isAfter(obj2Date)) {
        return 1;
      }
      if (obj1Date.isBefore(obj2Date)) {
        return -1;
      }
      return 0;
    });
  }

}
